// What every part of the Phoneware catalog shares: the products as last loaded, what
// the person may change, and the actions that change them. Each action calls one route
// from the plan's 3.6, replaces the product with what the server answered, and says
// what happened. Every Phoneware tier reads it; Billing ops and Super admins link it
// to OneBill and Autotask; only Super admins add products or change prices (mock M1).
import { createContext, useContext } from "react";
import type { AutotaskAction, BillingLinkRequest, CatalogPatchV2, CatalogSyncState } from "@phoneware/lens-domain";
import type { OneBillRow, Product } from "./model.js";

/** What a product's OneBill link becomes without choosing a OneBill plan. Create in OneBill chooses one to copy, so it goes through `link`. */
export type Decision = "not_billed" | "unreviewed";

export interface CatalogContext {
  /** catalog.link: OneBill links, dismissals, the Autotask fixes and the Autotask sync. */
  links: boolean;
  /** catalog.manage: add and import products, prices, floors, and what dealers see. */
  manages: boolean;
  list: Product[];
  /** The last catalog check's lists and counts, or null before the first check. */
  checked: CatalogSyncState | null;
  /** The product whose Enable Omni just refused, so its sheet can say why. */
  refused: string | null;
  /** What the server last refused, shown on the page and in an open sheet. */
  problem: string | null;
  busy: boolean;
  /** Reads the catalog again, after something outside these actions changed it. */
  reload(): void;
  open(id: string): void;
  diffs(id: string): void;
  add(from: OneBillRow | null): void;
  close(): void;
  /** PUT /api/catalog/:id/billing-link: a link, or Create in OneBill with the plan it copies. Resolves to the saved product, or null when the server refused (the error is shown). */
  link(p: Product, input: BillingLinkRequest, note: string): Promise<Product | null>;
  decide(p: Product, state: Decision): Promise<Product | null>;
  /** PATCH /api/catalog/:id {enabled}. Refuses on the spot, naming every reason, when the product can't be quoted. */
  enable(p: Product, on: boolean, from: "row" | "sheet"): Promise<void>;
  /** POST /api/catalog/:id/autotask. */
  autotask(p: Product, action: AutotaskAction): Promise<Product | null>;
  /** PATCH /api/catalog/:id with only the fields that changed, and the version the sheet opened. */
  save(p: Product, patch: Omit<CatalogPatchV2, "expectedUpdatedAt">): Promise<Product | null>;
}

const Ctx = createContext<CatalogContext | null>(null);

export const CatalogProvider = Ctx.Provider;

export function useCatalog(): CatalogContext {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useCatalog outside the Phoneware catalog");
  return ctx;
}

/** The loaded products, or an empty list outside the catalog. The intake review passes its own list. */
export function useCatalogList(): Product[] {
  return useContext(Ctx)?.list ?? [];
}
